import { buildAuthUrl } from './HttpService';

export const BADDOG_HANDOFF_TIMEOUT_MS = 15000;

export const BadDogAuthHandoffFailure = {
    ACCOUNT_NOT_CONFIGURED: 'account-not-configured',
    SERVICE_UNAVAILABLE: 'service-unavailable',
    NETWORK_UNAVAILABLE: 'network-unavailable',
    TIMEOUT: 'timeout',
    INVALID_RESPONSE: 'invalid-response',
};

export class BadDogAuthHandoffError extends Error {
    constructor(kind, message, metadata = {}) {
        super(message);
        this.name = 'BadDogAuthHandoffError';
        this.kind = kind;
        this.metadata = metadata;
    }
}

function isAccountNotConfigured(errorBody) {
    const message = errorBody?.message || errorBody?.msg || '';
    return String(message).toLowerCase().includes('authenticated user does not exist in telescope');
}

async function readErrorBody(response) {
    try {
        return await response.json();
    } catch {
        return null;
    }
}

/**
 * Exchanges an Entra ID token for BADDOG access and refresh tokens.
 * The request keeps the current BADDOG URL contract while giving callers
 * stable failure categories for availability and authorization handling.
 */
export async function handoffBadDogAuthToken(idToken, options = {}) {
    const timeoutMs = options.timeoutMs ?? BADDOG_HANDOFF_TIMEOUT_MS;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    const url = buildAuthUrl('auth/authhandoff', idToken);

    try {
        const response = await fetch(url, { signal: controller.signal });

        if (!response.ok) {
            const errorBody = await readErrorBody(response);
            if (isAccountNotConfigured(errorBody)) {
                throw new BadDogAuthHandoffError(
                    BadDogAuthHandoffFailure.ACCOUNT_NOT_CONFIGURED,
                    'The authenticated user is not configured in Telescope.',
                    { status: response.status },
                );
            }

            if ([502, 503, 504].includes(response.status)) {
                throw new BadDogAuthHandoffError(
                    BadDogAuthHandoffFailure.SERVICE_UNAVAILABLE,
                    'The authorization service is temporarily unavailable.',
                    { status: response.status },
                );
            }

            throw new BadDogAuthHandoffError(
                BadDogAuthHandoffFailure.INVALID_RESPONSE,
                'The authorization service returned an unexpected response.',
                { status: response.status },
            );
        }

        const tokenResponse = await response.json();
        if (!tokenResponse?.access_token || !tokenResponse?.refresh_token) {
            throw new BadDogAuthHandoffError(
                BadDogAuthHandoffFailure.INVALID_RESPONSE,
                'The authorization service returned an incomplete token response.',
            );
        }

        return tokenResponse;
    } catch (error) {
        if (error instanceof BadDogAuthHandoffError) {
            throw error;
        }

        if (error?.name === 'AbortError') {
            throw new BadDogAuthHandoffError(
                BadDogAuthHandoffFailure.TIMEOUT,
                'The authorization service did not respond in time.',
                { timeoutMs },
            );
        }

        throw new BadDogAuthHandoffError(
            BadDogAuthHandoffFailure.NETWORK_UNAVAILABLE,
            'The authorization service could not be reached.',
            { cause: error?.message },
        );
    } finally {
        clearTimeout(timeoutId);
    }
}